import axios from 'axios';

// WordPress API types
export interface WordPressEvent {
  id: number;
  date: string;
  slug?: string;
  link?: string;
  excerpt?: {
    rendered: string;
  };
  title: {
    rendered: string;
  };
  content: {
    rendered: string;
  };
  _embedded?: {
    'wp:featuredmedia'?: Array<{
      source_url: string;
    }>;
  };
  acf?: {
    event_date?: string;
    event_time?: string;
    closing_time?: string;
    price_description?: string;
    genre?: string;
    link_tickets?: string;
    link_streaming?: string;
    link_youtube?: string;
    link_instagram?: string;
    link_bandcamp?: string;
  };
}

export interface WordPressResponse {
  data: WordPressEvent[];
  headers: {
    'x-wp-total': string;
    'x-wp-totalpages': string;
  };
}

export interface WordPressPage {
  id: number;
  date: string;
  slug: string;
  link?: string;
  title: {
    rendered: string;
  };
  content: {
    rendered: string;
    protected?: boolean;
  };
  excerpt?: {
    rendered: string;
    protected?: boolean;
  };
  acf?: Record<string, unknown>;
}

export interface WordPressSponsor {
  id: number;
  date: string;
  link?: string;
  title: {
    rendered: string;
  };
  content?: {
    rendered: string;
  };
  _embedded?: {
    'wp:featuredmedia'?: Array<{
      source_url: string;
      alt_text?: string;
    }>;
  };
}

const normalizeApiBaseUrl = (value: string) => {
  const baseUrl = value.replace(/\/$/, '');

  return baseUrl.endsWith('/wp-json/wp/v2') ? baseUrl : `${baseUrl}/wp-json/wp/v2`;
};

const API_BASE_URL = normalizeApiBaseUrl(
  process.env.REACT_APP_WORDPRESS_API_URL || 'http://macm3.local:8000/wp-json/wp/v2'
);

const normalizeEventDate = (value?: string) => {
  if (!value || !/^\d{8}$/.test(value)) {
    return value;
  }

  return `${value.slice(6, 8)}/${value.slice(4, 6)}/${value.slice(0, 4)}`;
};

const normalizeEventTime = (value?: string) => {
  if (!value) {
    return value;
  }

  const match = value.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
  if (!match) {
    return value;
  }

  const [, hourValue, minutes, meridiem] = match;
  let hours = Number(hourValue);

  if (meridiem.toLowerCase() === 'pm' && hours !== 12) hours += 12;
  if (meridiem.toLowerCase() === 'am' && hours === 12) hours = 0;

  return `${hours.toString().padStart(2, '0')}:${minutes}`;
};

const normalizeEvent = (event: WordPressEvent): WordPressEvent => ({
  ...event,
  acf: {
    ...event.acf,
    event_date: normalizeEventDate(event.acf?.event_date),
    event_time: normalizeEventTime(event.acf?.event_time),
    closing_time: normalizeEventTime(event.acf?.closing_time),
  },
});

const buildApiBaseUrlCandidates = (): string[] => {
  const candidates = [API_BASE_URL];

  try {
    const parsedUrl = new URL(API_BASE_URL);
    const hostname = parsedUrl.hostname;

    if (hostname === 'macm3') {
      candidates.push(API_BASE_URL.replace('://macm3:', '://macm3.local:'));
    }

    if (typeof window !== 'undefined' && (hostname === 'macm3' || hostname === 'localhost')) {
      const browserHostname = window.location.hostname;

      if (browserHostname && browserHostname !== hostname) {
        const browserUrl = new URL(API_BASE_URL);
        browserUrl.hostname = browserHostname;
        candidates.push(browserUrl.toString());
      }
    }

    if (hostname !== 'localhost') {
      candidates.push(API_BASE_URL.replace(`://${hostname}:`, '://localhost:'));
    }
  } catch (_error) {
    return [API_BASE_URL];
  }

  return Array.from(new Set(candidates));
};

const requestWithFallback = async <T>(
  path: string,
  params?: Record<string, any>
): Promise<T> => {
  let lastError: unknown;

  for (const baseUrl of buildApiBaseUrlCandidates()) {
    try {
      const response = await axios.get<T>(`${baseUrl}${path}`, { params });
      return response.data;
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError;
};

const requestWithFallbackResponse = async <T>(
  path: string,
  params?: Record<string, any>
) => {
  let lastError: unknown;

  for (const baseUrl of buildApiBaseUrlCandidates()) {
    try {
      return await axios.get<T>(`${baseUrl}${path}`, { params });
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError;
};

export const wordpressApi = {
  async getEvents(orderby = 'event_date', order = 'asc', past = false): Promise<WordPressResponse> {
    try {
      const params: Record<string, any> = {
        _embed: true,
        // The stock WordPress REST API does not support custom ACF fields such as event_date.
        // Events are sorted by that field in the browser below.
        orderby: orderby === 'event_date' ? 'date' : orderby,
        order,
      };

      const response = await requestWithFallbackResponse<WordPressEvent[]>('/events', params);

      // Filter out past events
      const now = new Date();
      const normalizedEvents = response.data.map(normalizeEvent);
      const filteredEvents = normalizedEvents.filter(event => {
        const eventDateStr = event.acf?.event_date;
        if (!eventDateStr) return false;

        // Parse dd/mm/yyyy format
        const [day, month, year] = eventDateStr.split('/').map(Number);
        const eventDate = new Date(year, month - 1, day, 23, 59); // Month is 0-indexed in JavaScript Date

        return past ? eventDate < now : eventDate >= now;
      });

      const getEventTimestamp = (event: WordPressEvent) => {
        const eventDateStr = event.acf?.event_date;

        if (eventDateStr) {
          const [day, month, year] = eventDateStr.split('/').map(Number);
          const parsedEventDate = new Date(year, month - 1, day).getTime();

          if (!Number.isNaN(parsedEventDate)) {
            return parsedEventDate;
          }
        }

        return new Date(event.date).getTime();
      };

      const sortedEvents = [...filteredEvents].sort((eventA, eventB) => {
        const difference = getEventTimestamp(eventA) - getEventTimestamp(eventB);
        return order === 'asc' ? difference : -difference;
      });

      return {
        data: sortedEvents,
        headers: {
          'x-wp-total': response.headers['x-wp-total'],
          'x-wp-totalpages': response.headers['x-wp-totalpages'],
        },
      };
    } catch (error) {
      console.error('Error fetching events:', error);
      throw error;
    }
  },

  async getEventBySlug(slug: string): Promise<WordPressEvent | null> {
    try {
      const response = await requestWithFallbackResponse<WordPressEvent[]>('/events', {
        _embed: true,
        slug,
      });

      return response.data[0] ? normalizeEvent(response.data[0]) : null;
    } catch (error) {
      console.error('Error fetching event by slug:', error);
      throw error;
    }
  },

  async getPageBySlug(slug: string): Promise<WordPressPage | null> {
    try {
      const response = await requestWithFallback<WordPressPage[]>('/pages', {
        slug,
      });

      return response[0] ?? null;
    } catch (error) {
      console.error('Error fetching page by slug:', error);
      throw error;
    }
  },

  async getSponsors(): Promise<WordPressSponsor[]> {
    try {
      return await requestWithFallback<WordPressSponsor[]>('/sponsors', {
        _embed: true,
        orderby: 'date',
        order: 'asc',
        per_page: 100,
      });
    } catch (error) {
      console.error('Error fetching sponsors:', error);
      throw error;
    }
  },
};
